import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useTenant } from '../../context/TenantContext';
import { useCompany } from '../../context/CompanyContext';
import { useAuth } from '../../context/AuthContext';
import Icon from '../common/Icon';
import './Header.css';

const Header = ({ onOpenCommandPalette }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { currentTenant, tenants, selectTenant, exitImpersonation, isImpersonating } = useTenant();
  const { currentCompany, companies, selectCompany, exitCompanyView, isCompanyView } = useCompany();
  const { user, logout, isAuthenticated } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCompanyMenu, setShowCompanyMenu] = useState(false);
  const userMenuRef = useRef(null);
  const companyMenuRef = useRef(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (companyMenuRef.current && !companyMenuRef.current.contains(event.target)) {
        setShowCompanyMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCompanySelect = (companyId) => {
    selectCompany(companyId);
    setShowCompanyMenu(false);
    if (companyId !== 'all') {
      navigate(`/companies/${companyId}`);
    } else {
      navigate('/companies');
    }
  };

  const handleLogout = async () => {
    setShowUserMenu(false);
    await logout();
    navigate('/login');
  };

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return names[0].substring(0, 2).toUpperCase();
  };

  return (
    <header className="header">
      <div className="header-left">
        <div className="logo">Z101</div>

        {/* Company Switcher */}
        <div className="company-switcher" ref={companyMenuRef}>
          <button
            className="company-switcher-btn"
            onClick={() => setShowCompanyMenu(!showCompanyMenu)}
            aria-label="Select company"
            aria-expanded={showCompanyMenu}
          >
            {currentCompany.id !== 'all' && (
              <div
                className="company-switcher-logo"
                style={{ background: currentCompany.color }}
              >
                {currentCompany.logo}
              </div>
            )}
            <div className="company-switcher-info">
              <span className="company-switcher-name">{currentCompany.name}</span>
              <span className="company-switcher-domain">
                {currentCompany.domain || 'All companies'}
              </span>
            </div>
            <Icon name="chevron-down" className="company-switcher-arrow" />
          </button>

          {showCompanyMenu && (
            <div className="company-dropdown">
              <div className="company-dropdown-header">Switch Company</div>
              {companies.map(company => (
                <button
                  key={company.id}
                  className={`company-dropdown-item ${currentCompany.id === company.id ? 'active' : ''}`}
                  onClick={() => handleCompanySelect(company.id)}
                >
                  {company.id !== 'all' ? (
                    <div
                      className="company-dropdown-logo"
                      style={{ background: company.color }}
                    >
                      {company.logo}
                    </div>
                  ) : (
                    <div className="company-dropdown-logo all">
                      <Icon name="grid" />
                    </div>
                  )}
                  <div className="company-dropdown-info">
                    <span className="company-dropdown-name">{company.name}</span>
                    <span className="company-dropdown-domain">
                      {company.domain || 'Portfolio view'}
                    </span>
                  </div>
                  {currentCompany.id === company.id && (
                    <Icon name="check" className="company-dropdown-check" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Legacy Tenant Selector */}
        <div className="context-indicator">
          <span className="viewing-as">Tenant:</span>
          <select
            className="tenant-dropdown"
            id="tenant-selector"
            value={currentTenant}
            onChange={(e) => selectTenant(e.target.value)}
            aria-label="Select tenant"
          >
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
          {isImpersonating && (
            <button className="exit-btn" onClick={exitImpersonation}>
              Exit View
            </button>
          )}
        </div>
      </div>

      <div className="header-right">
        {/* Search Button (Opens Command Palette) */}
        <button
          className="header-icon-btn"
          aria-label="Search (Ctrl+K)"
          onClick={onOpenCommandPalette}
          title="Search (Ctrl+K)"
        >
          <Icon name="search" />
        </button>

        {/* Notifications */}
        <button className="header-icon-btn" aria-label="Notifications">
          <Icon name="bell" />
          <span className="notification-badge">3</span>
        </button>

        {/* Theme Toggle */}
        <button
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label="Toggle dark mode"
        >
          {theme === 'light' ? (
            <Icon name="sun" className="sun-icon" />
          ) : (
            <Icon name="moon" className="moon-icon" />
          )}
        </button>

        {/* User Menu */}
        <div className="user-menu-container" ref={userMenuRef}>
          <button
            className="user-avatar-btn"
            onClick={() => setShowUserMenu(!showUserMenu)}
            aria-label="User menu"
            aria-expanded={showUserMenu}
          >
            <div className="user-avatar">
              {getUserInitials()}
              <div className="status-dot"></div>
            </div>
          </button>

          {showUserMenu && (
            <div className="user-dropdown">
              <div className="user-dropdown-header">
                <div className="user-dropdown-avatar">{getUserInitials()}</div>
                <div className="user-dropdown-info">
                  <div className="user-dropdown-name">{user?.name || 'User'}</div>
                  <div className="user-dropdown-email">{user?.email || ''}</div>
                </div>
              </div>
              <div className="user-dropdown-divider"></div>
              <button className="user-dropdown-item" onClick={() => { setShowUserMenu(false); navigate('/settings'); }}>
                <Icon name="settings" />
                <span>Settings</span>
              </button>
              <button className="user-dropdown-item" onClick={() => { setShowUserMenu(false); navigate('/activity'); }}>
                <Icon name="activity" />
                <span>Activity Log</span>
              </button>
              <div className="user-dropdown-divider"></div>
              <button className="user-dropdown-item danger" onClick={handleLogout}>
                <Icon name="log-out" />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
